import React, { UIEvent, useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Avatar, Dialog, IconButton } from '@mui/material';
import Linkify from "linkify-react";
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CloseIcon from '@mui/icons-material/Close';
import type { Message, Participant, User as TwilioUser, Conversation } from '@twilio/conversations';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { getUrlElement } from './../../utils/common';
import FtvIcon from '../../utils/FtvIcon'; // Commented out - module not found
import RichMediaMessage from '../../components/RichMediaMessage';
import { ACTION_UPDATE_CONVERSATION_STATE } from '../../twilio/store/actionTypes';
import UsernameInput from './UsernameInput';
import DOBInput from './DOBInput';
import OcrUploadInput from './OcrUploadInput';
import BankStatementUploadInput from './BankStatementUploadInput';
import { sendOnHoldMessage } from '../../utils/sendOnHoldMessageApi'
import {initParticipantsListener} from '../../twilio/store'
type Props = {
  messages: Message[];
  participants: Participant[];
  users?: TwilioUser[];
  myIdentity?: string;
  onScroll?: (e: UIEvent<HTMLDivElement>) => void;
  listRef?: React.RefObject<HTMLDivElement>;
  isLoadingOlder?: boolean;
  showTopSpinner?: boolean;
  onRichMediaAction?: (action: any) => void;
  conversation?: Conversation;
  disabledRichMediaSids: Set<string>;
  richMediaClickHistory?: { key: string; value: string }[];
};
let brand = getUrlElement(7);
let isFtv = (brand === 'ftv') ? true : false;
const spinnerBoxStyles = { display: 'flex', justifyContent: 'center', py: 1 } as const;

const bubbleStyles = {
  maxWidth: '65%',
  p: '8px 12px',
  borderRadius: '14px',
  wordBreak: 'break-word',
} as const;

const listContainerStyles = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 12px 0 12px',
};

function formatFileSize(size?: number) {
  if (!size || size <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = unitIndex === 0 ? value.toString() : value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
}

function isSameDay(a?: Date, b?: Date) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function MessageList({ messages = [], participants = [], users = [], myIdentity, onScroll, listRef, isLoadingOlder, showTopSpinner, onRichMediaAction, conversation, disabledRichMediaSids, richMediaClickHistory = [] }: Props) {
  // Replaces a rich media payload (the raw value sent as the message body) with the
  // button's display text, using the key/value pairs recorded when the button was clicked.
  const getRichMediaDisplayText = (body: string) => {
    const match = richMediaClickHistory.find((entry) => entry.key === body);
    return match ? match.value : body;
  };

  const [focusIndex, setFocusIndex] = useState(messages?.length ? messages[messages.length - 1].index : -1);
  const [richMediaMessages, setRichMediaMessages] = useState<Set<string>>(new Set());
  const [usernameInputMessages, setUsernameInputMessages] = useState<Set<string>>(new Set());
  const [dobInputMessages, setDobInputMessages] = useState<Set<string>>(new Set());
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const mediaUrlCacheRef = useRef<Record<string, string>>({});
  const [ocrUploadMessages, setOcrUploadMessages] = useState<Set<string>>(new Set());
  const [completedOcrUploadMessages, setCompletedOcrUploadMessages] = useState<Set<string>>(new Set());
  const [bankStatementUploadMessages, setBankStatementUploadMessages] = useState<Set<string>>(new Set());
  const [completedBankStatementUploadMessages, setCompletedBankStatementUploadMessages] = useState<Set<string>>(new Set());
  const dispatch = useDispatch();
  const conversationSid = useSelector(
    (state: RootState) => state.user?.user?.conversationSid
  );
  const onHoldIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isOnHoldRef = useRef(false);
  
  // Function to check if a message requires username input
  const checkForUsernameInput = async (message: Message): Promise<boolean> => {
    try {
      const metadata = await message.getChannelMetadata();
      if (metadata && metadata.type === 'dialogflowcx') {
        const queryResult = (metadata.data as any)?.queryResult;
        const currentPage = queryResult?.currentPage;
        const displayName = currentPage?.displayName;

        console.log('🔍 Checking message for username input:', {
          messageIndex: message.index,
          displayName,
          currentPage,
          queryResult
        });

        return displayName === 'username';
      }
    } catch (error) {
      console.error('Error checking message metadata for username input:', error);
    }
    return false;
  };

  // Function to check if a message requires DOB input
  const checkForDOBInput = async (message: Message): Promise<boolean> => {
    try {
      const metadata = await message.getChannelMetadata();
      if (metadata && metadata.type === 'dialogflowcx') {
        const queryResult = (metadata.data as any)?.queryResult;
        const currentPage = queryResult?.currentPage;
        const displayName = currentPage?.displayName;

        console.log('🔍 Checking message for DOB input:', {
          messageIndex: message.index,
          displayName,
          currentPage,
          queryResult
        });

        return displayName === 'dob';
      }
    } catch (error) {
      console.error('Error checking message metadata for DOB input:', error);
    }
    return false;
  };

  // Function to check if a message requires OCR upload input
  const checkForOcrUpload = async (message: Message): Promise<boolean> => {
    try {
      const metadata = await message.getChannelMetadata();
      if (metadata && metadata.type === 'dialogflowcx') {
        const queryResult = (metadata.data as any)?.queryResult;
        const currentPage = queryResult?.currentPage;
        const displayName = currentPage?.displayName;

        console.log('🔍 Checking message for OCR upload input:', {
          messageIndex: message.index,
          displayName,
          currentPage,
          queryResult
        });

        return displayName === 'ocr-upload';
      }
    } catch (error) {
      console.error('Error checking message metadata for OCR upload input:', error);
    }
    return false;
  };

  // Function to check if a message requires bank statement upload input
  const checkForBankStatementUpload = async (message: Message): Promise<boolean> => {
    try {
      const metadata = await message.getChannelMetadata();
      if (metadata && metadata.type === 'dialogflowcx') {
        const queryResult = (metadata.data as any)?.queryResult;
        const currentPage = queryResult?.currentPage;
        const displayName = currentPage?.displayName;

        console.log('🔍 Checking message for bank statement upload input:', {
          messageIndex: message.index,
          displayName,
          currentPage,
          queryResult
        });

        return displayName === 'bank-statement-upload';
      }
    } catch (error) {
      console.error('Error checking message metadata for bank statement upload input:', error);
    }
    return false;
  };

const startOnHoldMessages = () => {
  if (isOnHoldRef.current) return;

  isOnHoldRef.current = true;

  const sendOnHold = () => {
    if (!conversationSid) return;

    sendOnHoldMessage({
      conversationSid,
      messageBody:
        'Please stay online while we connect you to our Online Support Representative.',
      author: 'Support Agent'
    });
  };

  sendOnHold(); // immediate
  onHoldIntervalRef.current = setInterval(sendOnHold, 3 * 60 * 1000);
};

const stopOnHoldMessages = () => {
  if (onHoldIntervalRef.current) {
    clearInterval(onHoldIntervalRef.current);
    onHoldIntervalRef.current = null;
  }

  isOnHoldRef.current = false;
};


  // Function to check a single message for rich media content
  const checkSingleMessageForRichMedia = async (message: Message) => {
    try {
      // Skip if already processed
      if ((message as any)._richMediaChecked) {
        return;
      }

      // Mark as checked immediately
      (message as any)._richMediaChecked = true;

      console.log(`🎯 SINGLE MESSAGE CHECK - Processing message ${message.index} (${message.sid}): "${message.body}"`);

      // Check for username input first
      const needsUsernameInput = await checkForUsernameInput(message);
      if (needsUsernameInput) {
        console.log(`🎯 Message ${message.index} requires username input`);
        setUsernameInputMessages(prev => new Set(prev).add(message.index.toString()));
        return;
      }

      // Check for DOB input
      const needsDOBInput = await checkForDOBInput(message);
      if (needsDOBInput) {
        console.log(`🎯 Message ${message.index} requires DOB input`);
        setDobInputMessages(prev => new Set(prev).add(message.index.toString()));
        return;
      }

      // Check for OCR upload input
      const needsOcrUpload = await checkForOcrUpload(message);
      if (needsOcrUpload) {
        console.log(`🎯 Message ${message.index} requires OCR upload input`);
        setOcrUploadMessages(prev => new Set(prev).add(message.index.toString()));
        return;
      }

      // Check for bank statement upload input
      const needsBankStatementUpload = await checkForBankStatementUpload(message);
      if (needsBankStatementUpload) {
        console.log(`🎯 Message ${message.index} requires bank statement upload input`);
        setBankStatementUploadMessages(prev => new Set(prev).add(message.index.toString()));
        return;
      }

      // Check if the message has getChannelMetadata method
      if (typeof message.getChannelMetadata === 'function') {
        try {
          const metadata = await message.getChannelMetadata();

          if (metadata && metadata !== null) {
            console.log('🎯 Rich Media Message Detected via getChannelMetadata:', metadata);

            // Check for Dialogflow CX rich media content
            if (metadata.type === 'dialogflowcx' && (metadata.data as any)?.queryResult?.responseMessages) {
              const responseMessages = (metadata.data as any).queryResult.responseMessages;
              console.log(`📦 Message ${message.index} has ${responseMessages.length} response messages`);

              // Check for sataAction: "sata-end-chat" parameter to close conversation
              const queryResult = (metadata.data as any)?.queryResult;
              if (queryResult?.parameters?.sataAction === "sata-end-chat") {
                console.log(`🚪 SATA END CHAT ACTION DETECTED in message ${message.index} - Closing conversation`);

                if (conversation) {
                  try {
                    await conversation.leave();
                    console.log('✅ Successfully left conversation due to sata-end-chat action');

                    // Update conversation state to 'closed' in Redux
                    dispatch({
                      type: ACTION_UPDATE_CONVERSATION_STATE,
                      payload: { conversationState: 'closed' }
                    });
                    console.log('🔄 Conversation state updated to closed in Redux');
                  } catch (error) {
                    console.error('❌ Error leaving conversation:', error);
                  }
                } else {
                  console.warn('⚠️ No conversation available to close');
                }

                return; // Exit early since we're closing the conversation
              }
              if (queryResult?.parameters?.sataAction === "sata-transfer-chat") {

                if (conversation) {
                  startOnHoldMessages()
                } else {
                  console.warn('error is occured in send on hold message function');
                }
                return; 
              }
              // Check each response message for actions
              let hasActions = false;
              responseMessages.forEach((response: any, idx: number) => {
                console.log("🔍 Response structure:", response);

                // Check for actions in both possible locations
                const actions = response.payload?.payload?.actions || response.payload?.body?.payload?.actions;

                if (actions && actions.length > 0) {
                  console.log(`🔘 Response ${idx} has ${actions.length} interactive actions:`, actions);
                  hasActions = true;
                }
              });

              if (hasActions) {
                (message as any)._richMediaMetadata = metadata;
                console.log(`✨ Message ${message.index} marked with rich media metadata for rendering`);

                // Add to rich media messages set to trigger re-render
                setRichMediaMessages(prev => {
                  const newSet = new Set(prev);
                  newSet.add(message.sid);
                  console.log(`🔄 Added message ${message.sid} to rich media set, triggering re-render`);
                  return newSet;
                });
              }
            }
          } else {
            console.log(`📝 Message ${message.index}: getChannelMetadata returned null`);
          }
        } catch (metadataError: any) {
          if (
            metadataError?.message?.includes('Fetch resource') ||
            metadataError?.message?.includes('failed') ||
            metadataError?.status === 404 ||
            metadataError?.status === 400
          ) {
            console.log(`📝 Message ${message.index}: No channel metadata available (${metadataError.status || 'fetch failed'})`);
          } else {
            console.log(`⚠️ Message ${message.index} getChannelMetadata error:`, metadataError.message || metadataError);
          }
        }
      } else {
        console.log(`📝 Message ${message.index}: getChannelMetadata method not available`);
      }
    } catch (error) {
      console.error(`❌ Error processing single message ${message.index}:`, error);
    }
  };

  // derive my identity the same way as the reference code (from conversationsClient in redux)
  const conversationsClient = useSelector((state: RootState) => (state as any)?.chat?.conversationsClient);
  const myIdentityDerived = conversationsClient?.user?.identity;
  const isSelf = (author?: string | null) => author === (myIdentity ?? myIdentityDerived);

  // Track previous message count to detect new messages
  const [previousMessageCount, setPreviousMessageCount] = useState(0);

  // Reset message count tracking when conversation changes (e.g., after restart)
  useEffect(() => {
    if (conversation) {
      console.log('🔄 Conversation changed - resetting message count tracking');
      setPreviousMessageCount(0);
      // Clear all message tracking sets for fresh start
      setRichMediaMessages(new Set());
      setUsernameInputMessages(new Set());
      setDobInputMessages(new Set());
      setOcrUploadMessages(new Set());
      setCompletedOcrUploadMessages(new Set());
      setBankStatementUploadMessages(new Set());
      setCompletedBankStatementUploadMessages(new Set());
    }
  }, [conversation]);

  // Check for rich media messages using getChannelMetadata - only for NEW messages
  useEffect(() => {
    
    const checkNewMessages = async () => {
      const currentMessageCount = messages.length;

      if (currentMessageCount > previousMessageCount) {
        console.log(`🚀 NEW MESSAGE DETECTED! Count changed from ${previousMessageCount} to ${currentMessageCount}`);

        // Process only the new messages (from previousMessageCount to currentMessageCount)
        const newMessages = messages.slice(previousMessageCount);

        for (const message of newMessages) {
          console.log(`🎯 PROCESSING NEW MESSAGE ${message.index}: "${message.body}"`);
          await checkSingleMessageForRichMedia(message);
        }

        // Update the previous count
        setPreviousMessageCount(currentMessageCount);
      } else if (currentMessageCount === 0 && previousMessageCount > 0) {
        // Reset when messages are cleared (e.g., during chat restart)
        console.log('🔄 Messages cleared - resetting message count tracking for fresh chat');
        setPreviousMessageCount(0);
        // Clear all message tracking sets for fresh start
        setRichMediaMessages(new Set());
        setUsernameInputMessages(new Set());
        setDobInputMessages(new Set());
        setOcrUploadMessages(new Set());
        setCompletedOcrUploadMessages(new Set());
        setBankStatementUploadMessages(new Set());
        setCompletedBankStatementUploadMessages(new Set());
      }
    };

    checkNewMessages();
  }, [messages, previousMessageCount]);



  useEffect(() => {
    setFocusIndex(messages?.length ? messages[messages.length - 1].index : -1);
  }, [messages]);

  // Resolve temporary URLs for message attachments (images/files)
  useEffect(() => {
    let cancelled = false;

    const resolveAttachmentUrls = async () => {
      for (const message of messages) {
        const attachedMedia = Array.from((((message as any)?.attachedMedia ?? []) as any[]));
        if (!attachedMedia.length) continue;

        for (let mediaIndex = 0; mediaIndex < attachedMedia.length; mediaIndex += 1) {
          const media = attachedMedia[mediaIndex];
          const mediaSid = media?.sid ?? media?.state?.sid ?? `${mediaIndex}`;
          const mediaKey = `${message.sid}:${mediaSid}`;

          if (mediaUrlCacheRef.current[mediaKey]) continue;

          try {
            let temporaryUrl = '';

            if (typeof media?.getContentTemporaryUrl === 'function') {
              temporaryUrl = await media.getContentTemporaryUrl();
            } else if (typeof media?.getCachedTemporaryUrl === 'function') {
              temporaryUrl = await media.getCachedTemporaryUrl();
            } else if (media?.contentUrl) {
              // Message synced in from another DafaPlus tab via localStorage - the
              // originating tab already resolved this URL through the live SDK object.
              temporaryUrl = media.contentUrl;
            }

            if (!temporaryUrl) continue;

            mediaUrlCacheRef.current[mediaKey] = temporaryUrl;

            if (!cancelled) {
              setMediaUrls((prev) => (prev[mediaKey] ? prev : { ...prev, [mediaKey]: temporaryUrl }));
            }
          } catch (error) {
            console.error('Error resolving attachment media URL:', error);
          }
        }
      }
    };

    resolveAttachmentUrls();

    return () => {
      cancelled = true;
    };
  }, [messages]);

  // Auto-scroll to bottom when messages change (including participant events and rich media)
  useEffect(() => {
    if (listRef?.current) {
      const scrollElement = listRef.current;
      // Use setTimeout to ensure DOM has updated before scrolling
      setTimeout(() => {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 0);
    }
  }, [messages, richMediaMessages, listRef]);

  useEffect(() => {
    messages?.forEach((m) => {
      const identity = m.author ?? '(no author)';
      const participant = participants?.find((p) => p.identity === identity);
      const type = (participant as any)?.type ?? 'unknown';
      console.log('MessageList: participant', { identity, type, message: m, participant });
    });
  }, [messages, participants]);

  // Check for username input messages
  useEffect(() => {
    const checkUsernameInputMessages = async () => {
      const newUsernameInputMessages = new Set<string>();

      for (const message of messages) {
        const isUsernameInput = await checkForUsernameInput(message);
        if (isUsernameInput) {
          newUsernameInputMessages.add(message.index.toString());
          console.log('🎯 Username input message detected:', message.index);
        }
      }

      setUsernameInputMessages(newUsernameInputMessages);
    };

    const checkBOBInputMessages = async () => {
      const newDOBInputMessages = new Set<string>();

      for (const message of messages) {
        const isUsernameInput = await checkForDOBInput(message);
        if (isUsernameInput) {
          newDOBInputMessages.add(message.index.toString());
          console.log('🎯 DOB input message detected:', message.index);
        }
      }

      setDobInputMessages(newDOBInputMessages);
    };

    const checkOcrUploadMessages = async () => {
      const newOcrUploadMessages = new Set<string>();

      for (const message of messages) {
        const messageId = message.index.toString();

        if (completedOcrUploadMessages.has(messageId)) {
          continue;
        }

        const isOcrUpload = await checkForOcrUpload(message);
        if (isOcrUpload) {
          newOcrUploadMessages.add(messageId);
          console.log('🎯 OCR upload input message detected:', message.index);
        }
      }

      setOcrUploadMessages(newOcrUploadMessages);
    };

    const checkBankStatementUploadMessages = async () => {
      const newBankStatementUploadMessages = new Set<string>();

      for (const message of messages) {
        const messageId = message.index.toString();

        if (completedBankStatementUploadMessages.has(messageId)) {
          continue;
        }

        const isBankStatementUpload = await checkForBankStatementUpload(message);
        if (isBankStatementUpload) {
          newBankStatementUploadMessages.add(messageId);
          console.log('🎯 Bank statement upload input message detected:', message.index);
        }
      }

      setBankStatementUploadMessages(newBankStatementUploadMessages);
    };

    checkUsernameInputMessages();
    checkBOBInputMessages();
    checkOcrUploadMessages();
    checkBankStatementUploadMessages();
  }, [messages, completedOcrUploadMessages, completedBankStatementUploadMessages]);

useEffect(() => {
  if (!conversation) return;

  initParticipantsListener(
    conversation,
    dispatch,
    stopOnHoldMessages // 👈 this is the key link
  );

  return () => stopOnHoldMessages();
}, [conversation]);

  const handleFocus = () => {
    if (messages?.length && focusIndex < 0) {
      setFocusIndex(messages[messages.length - 1].index);
    }
  };

  const openImagePreview = (url: string, name: string) => {
    if (!url) return;
    setPreviewImage({ url, name });
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: '12px' }}
      ref={listRef}
      onScroll={onScroll}
      role="log"
      aria-relevant="additions"
      tabIndex={focusIndex >= 0 ? -1 : 0}
      onFocus={handleFocus}
    >
      {showTopSpinner && (
        <Box sx={spinnerBoxStyles}>
          <CircularProgress size={20} />
        </Box>
      )}

      <Box sx={listContainerStyles} role="log" aria-relevant="additions" tabIndex={focusIndex >= 0 ? -1 : 0} onFocus={handleFocus}>
        {messages.map((m: Message, i: number) => {
          const prev = messages[i - 1];
          const showDateSep = i === 0 || !isSameDay(prev?.dateCreated as any, m.dateCreated as any);
          const author = m.author || '';
          const isMine = author === (myIdentity ?? myIdentityDerived) || /^FX[a-f0-9]{32}$/i.test(author);

          // Check if this is a participant event message
          const isParticipantEvent = (m as any).isSystemMessage && (m as any).type === 'participant_event';

          // Debug: Check if this message has rich media metadata
          const hasRichMedia = !!(m as any)._richMediaMetadata;

          // Check if this message requires username input
          const isUsernameInputMessage = usernameInputMessages.has(m.index.toString());

          // Check if this message requires DOB input
          const isDOBInputMessage = dobInputMessages.has(m.index.toString());
          const attachedMedia = Array.from((((m as any)?.attachedMedia ?? []) as any[]));
          const hasAttachedMedia = attachedMedia.length > 0;

          // Check if this message requires OCR upload input
          const isOcrUploadMessage = ocrUploadMessages.has(m.index.toString());
          const isCompletedOcrUploadMessage = completedOcrUploadMessages.has(m.index.toString());
          const isBankStatementUploadMessage = bankStatementUploadMessages.has(m.index.toString());
          const isCompletedBankStatementUploadMessage = completedBankStatementUploadMessages.has(m.index.toString());
          const hasTextContent = (m?.body?.trim() ?? '').length > 0;
          const hasVisibleBubbleContent =
            hasTextContent ||
            (hasAttachedMedia && isMine) ||
            !!(m as any)._richMediaMetadata ||
            (isUsernameInputMessage && !isMine) ||
            (isDOBInputMessage && !isMine) ||
            (isOcrUploadMessage && !isMine) ||
            (isCompletedOcrUploadMessage && !isMine) ||
            (isBankStatementUploadMessage && !isMine) ||
            (isCompletedBankStatementUploadMessage && !isMine);

          if (!isParticipantEvent && !hasVisibleBubbleContent) {
            return null;
          }

          return (
            <React.Fragment key={m.index}>


              {/* Render participant event messages differently */}
              {isParticipantEvent ? (
                  hasTextContent && <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
                  <Box sx={{ flex: 1, height: 1, background: "#eee" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#888",
                      fontSize: "12px",
                      fontStyle: "italic",
                      mx: 1
                    }}
                  >
                    {m.body as string}
                  </Typography>
                  <Box sx={{ flex: 1, height: 1, background: "#eee" }} />
                </Box>
              ) : (

              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  mb: 1.5,
                  alignItems: "flex-end",
                  justifyContent: isMine ? "flex-end" : "flex-start",
                  gap: 1.2,
                }}
              >
                {!isMine && (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "#e9e9ee",
                      color: "#777",
                      fontSize: 16,
                      boxShadow: "inset 0 0 0 1px #d6d6db",
                    }}
                  >
                    {isFtv ? (
                      <div>
                        <FtvIcon  />
                      </div>
                    ) : (
                      <div>
                        <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />
                      </div>
                    )}
                  </Avatar>
                )}
                
                {hasVisibleBubbleContent && <Box
                  sx={{
                    ...bubbleStyles,
                    ml: isMine ? "auto" : 0,
                    bgcolor: isMine ? "#4B4B4B" : "#E5E5EB",
                    color: isMine ? "#fff" : "#111",
                    borderTopRightRadius: isMine ? 4 : 14,
                    borderTopLeftRadius: isMine ? 14 : 4,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                    {m.dateCreated && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isMine ? "#ccc" : "#666",
                        fontSize: 11,
                        mt: 0.5,
                      }}
                    >
                      { m.dateCreated.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit"
                      }) }
                    </Typography>
                  )}
                  {hasTextContent && (
                    <Typography
                      variant="body2"
                      sx={{
                        "& a": {
                          color: isMine ? "#90CAF9" : "#1976D2",
                          textDecoration: "underline",
                          wordBreak: "break-all",
                        },
                      }}
                    >
                      <Linkify
                        options={{
                          target: "_blank",
                          rel: "noopener noreferrer",
                        }}
                      >
                        {getRichMediaDisplayText(m.body as string)}
                      </Linkify>
                    </Typography>
                  )}

                  {hasAttachedMedia && isMine && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {attachedMedia.map((media: any, mediaIndex: number) => {
                        const mediaSid = media?.sid ?? media?.state?.sid ?? `${mediaIndex}`;
                        const mediaKey = `${m.sid}:${mediaSid}`;
                        const mediaUrl = mediaUrls[mediaKey];
                        const contentType = media?.contentType ?? media?.state?.contentType ?? '';
                        const isImage = contentType.startsWith('image/');
                        const filename = media?.filename ?? media?.state?.filename ?? 'Attachment';
                        const size = media?.size ?? media?.state?.size;

                        return (
                          <Box
                            key={mediaKey}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              overflow: 'hidden',
                              maxWidth: 240,
                            }}
                          >
                            {isImage && mediaUrl ? (
                              <Box
                                component="button"
                                type="button"
                                onClick={() => openImagePreview(mediaUrl, filename)}
                                sx={{
                                  display: 'block',
                                  lineHeight: 0,
                                  width: '100%',
                                  p: 0,
                                  border: 0,
                                  background: 'transparent',
                                }}
                              >
                                <Box
                                  component="img"
                                  src={mediaUrl}
                                  alt={filename}
                                  sx={{
                                    width: '100%',
                                    maxHeight: 240,
                                    objectFit: 'contain',
                                    cursor: 'pointer',
                                    display: 'block',
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                component={mediaUrl ? 'a' : 'div'}
                                href={mediaUrl || undefined}
                                target={mediaUrl ? '_blank' : undefined}
                                rel={mediaUrl ? 'noopener noreferrer' : undefined}
                                sx={{
                                  display: 'block',
                                  p: 1,
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  cursor: mediaUrl ? 'pointer' : 'default',
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {filename}
                                </Typography>
                                <Typography variant="caption" sx={{ color: isMine ? '#ccc' : '#666' }}>
                                  {contentType || 'File'}{size ? ` • ${formatFileSize(size)}` : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Render rich media content if available */}
                  {(m as any)._richMediaMetadata && (
                    <Box sx={{ mt: 1 }}>
                      <RichMediaMessage
                        metadata={(m as any)._richMediaMetadata}
                        onActionClick={onRichMediaAction}
                        isMine={isMine}
                        forceDisabled={disabledRichMediaSids.has(m.sid)}
                      />
                    </Box>
                  )}

                  {/* Render username input if this message requires it */}
                  {isUsernameInputMessage && !isMine && (
                    <Box sx={{ mt: 1 }}>
                      <UsernameInput
                        conversation={conversation}
                        onSubmit={(username) => {
                          console.log('Username submitted from message:', username);
                          // Optional: Remove this message from username input messages after submission
                          setUsernameInputMessages(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(m.index.toString());
                            return newSet;
                          });
                        }}
                      />
                    </Box>
                  )}

                  {/* Render DOB input if this message requires it */}
                  {isDOBInputMessage && !isMine && (
                    <Box sx={{ mt: 1 }}>
                      <DOBInput
                        conversation={conversation}
                        onSubmit={(dob) => {
                          console.log('DOB submitted from message:', dob);
                          // Optional: Remove this message from DOB input messages after submission
                          setDobInputMessages(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(m.index.toString());
                            return newSet;
                          });
                        }}
                      />
                    </Box>
                  )}

                  {(isOcrUploadMessage || isCompletedOcrUploadMessage) && !isMine && (
                    <Box sx={{ mt: 1 }}>
                      <OcrUploadInput
                        conversation={conversation}
                        isCompleted={isCompletedOcrUploadMessage}
                        onSubmit={() => {
                          const messageId = m.index.toString();

                          setCompletedOcrUploadMessages(prev => {
                            const newSet = new Set(prev);
                            newSet.add(messageId);
                            return newSet;
                          });
                        }}
                      />
                    </Box>
                  )}

                  {(isBankStatementUploadMessage || isCompletedBankStatementUploadMessage) && !isMine && (
                    <Box sx={{ mt: 1 }}>
                      <BankStatementUploadInput
                        conversation={conversation}
                        isCompleted={isCompletedBankStatementUploadMessage}
                        onSubmit={() => {
                          const messageId = m.index.toString();

                          setCompletedBankStatementUploadMessages(prev => {
                            const newSet = new Set(prev);
                            newSet.add(messageId);
                            return newSet;
                          });
                        }}
                      />
                    </Box>
                  )}
                </Box>}
                {hasTextContent && isMine  && (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "#4B4B4B",
                      color: "#fff",
                      fontSize: 14,
                    }}
                  >
                    {myIdentity?.slice(0, 1)?.toUpperCase?.() || "Me"}
                  </Avatar>
                )}
              </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      <Dialog
        open={!!previewImage}
        onClose={closeImagePreview}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: 'relative', bgcolor: '#111', p: 1 }}>
          <IconButton
            onClick={closeImagePreview}
            aria-label="close image preview"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              sx={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
